import { useRef, useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { ButtonBase } from '#src/atoms/buttons/ButtonBase/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconApple } from '#src/atoms/icons/system/iconApple.ts';
import { iconInfoCircle } from '#src/atoms/icons/system/iconInfoCircle.ts';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { Tooltip } from '#src/atoms/tooltip/Tooltip/index.tsx';
import { Bold } from '#src/atoms/typography/Bold/index.tsx';
import { BodyM, BodyS, HeadlineM } from '#src/atoms/typography/index.tsx';
import { Dialog } from '#src/components/dialog/index.tsx';
import type { GraphQLResult } from '#src/components/form/Form/index.tsx';
import { GraphqlForm, TextField } from '#src/components/form/Form/index.tsx';
import { TwoFADialog } from '#src/components/form/TwoFADialog/index.tsx';
import type { GoogleReCAPTCHA } from '#src/components/recaptcha/index.tsx';
import { ReCAPTCHA } from '#src/components/recaptcha/index.tsx';
import { DisabledEmailWarning } from '#src/components/user/DisabledEmailWarning/index.tsx';
import type { UpdateUserEmailAttributes } from '#src/contexts/auth/index.ts';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useIntlContext } from '#src/contexts/intl/index.ts';
import { useWalletContext } from '#src/contexts/wallet/index.ts';
import { useScreenSize } from '#src/hooks/device/useScreenSize.ts';
import { isApplePrivateRelayEmail } from '#src/lib/email.ts';
import { glossary, userAttributes } from '#src/lib/glossary.ts';
import type { GraphQLError } from '#src/lib/gql/index.ts';
import { tabletAndAbove } from '#src/style/mediaQuery.ts';

import { UpdateEmailDialog } from './UpdateEmailDialog/index.tsx';

const messages = defineMessages({
  changeEmail: {
    defaultMessage: 'Change email',
  },
  privateRelay: {
    defaultMessage:
      'Apple has provided this anonymized email address (<b>{email}</b>) to protect your privacy. Any emails sent to this address will be automatically forwarded to your real email. If you would like to update your email address, please enter your preferred email using the link below.',
  },
});

const Body = styled(Vertical)`
  padding: var(--triple-unit);
`;
const Container = styled(GraphqlForm)`
  display: flex;
  flex-direction: column;
  margin-bottom: 0;
  gap: var(--double-unit);
  @media ${tabletAndAbove} {
    align-items: stretch;
  }
`;
const StyledButtonBase = styled(ButtonBase)`
  color: var(--c-link);
  text-decoration: underline;
`;

export const UpdateEmail = () => {
  const [openDialogEmail, setOpenDialogEmail] = useState<string | null>(null);
  const [openDialogForm, setOpenDialogForm] = useState<boolean>(false);
  const [open2FA, setOpen2FA] = useState<boolean>(false);
  const [hasChanged, setHasChanged] = useState(false);
  const [twoFACallback, setTwoFACallback] = useState<{
    resolve: any;
    reject: any;
  } | null>(null);

  const { up: isTablet } = useScreenSize('tablet');

  const recaptchaRef = useRef<GoogleReCAPTCHA>(null);

  const { formatMessage } = useIntlContext();
  const { currentUser } = useCurrentUserContext();
  const { updateUserEmailWithPassword } = useWalletContext();

  if (!currentUser) return null;

  const { email, otpRequiredForLogin } = currentUser;

  const submitUpdateUserMethodFactory: <T extends { email: string }>(
    callback: (attributes: T) => Promise<{ errors: GraphQLError[] } | null>
  ) => (
    attributes: T,
    doOnResult: (result: GraphQLResult) => void
  ) => Promise<void> =
    <T extends { email: string }>(callback: (attributes: T) => Promise<any>) =>
    async (attributes: T, doOnResult: any) => {
      if (!attributes.email || attributes.email === email) {
        return Promise.reject();
      }

      recaptchaRef.current?.reset();
      const recaptchaTokenV2 = await recaptchaRef.current?.executeAsync();

      if (recaptchaTokenV2) {
        const result = await callback({ ...attributes, recaptchaTokenV2 });

        doOnResult(result);
        if (!result.errors || result.errors.length === 0) {
          setOpenDialogEmail(attributes.email);
          setOpenDialogForm(false);
        }
        return Promise.resolve();
      }
      return Promise.reject();
    };

  const submitUpdateUser = async (
    attributes: UpdateUserEmailAttributes,
    doOnResult: (result: GraphQLResult) => void
  ) => {
    let abort = false;
    const otpAttempt = await new Promise((resolve, reject) => {
      if (!otpRequiredForLogin) return resolve(null);
      setTwoFACallback({ resolve, reject });
      setOpen2FA(true);
      return null;
    }).catch(() => {
      abort = true;
    });

    if (abort) return () => {};
    return submitUpdateUserMethodFactory(updateUserEmailWithPassword)(
      { ...attributes, ...(otpRequiredForLogin ? { otpAttempt } : {}) },
      doOnResult
    );
  };

  return (
    <div>
      <BodyM>
        <FormattedMessage {...glossary.accountEmail} />
      </BodyM>
      <BodyS color="var(--c-nd-600)">
        {isApplePrivateRelayEmail(email) ? (
          <Horizontal>
            <FontAwesomeIcon icon={iconApple} /> Apple Private Relay
            <Tooltip
              placement="top"
              title={
                <FormattedMessage
                  {...messages.privateRelay}
                  values={{ email, b: Bold }}
                />
              }
            >
              <FontAwesomeIcon icon={iconInfoCircle} />
            </Tooltip>
          </Horizontal>
        ) : (
          email
        )}
      </BodyS>
      <div>
        <StyledButtonBase onClick={() => setOpenDialogForm(true)}>
          <BodyS bold color="var(--c-link)">
            <FormattedMessage {...messages.changeEmail} />
          </BodyS>
        </StyledButtonBase>
      </div>
      <Dialog
        maxWidth="xs"
        fullWidth
        open={openDialogForm}
        onClose={() => setOpenDialogForm(false)}
        title={
          <HeadlineM as="h2" className="text-center">
            <FormattedMessage {...messages.changeEmail} />
          </HeadlineM>
        }
        fullScreen={!isTablet}
      >
        <Body gap={2}>
          <DisabledEmailWarning />
          <Container
            onSubmit={(values, onResult) => {
              submitUpdateUser(
                { ...values, email: values.email.toLowerCase() },
                onResult
              );
            }}
            onChange={values => {
              setHasChanged(email !== values.email);
            }}
            onSuccess={() => setOpenDialogForm(false)}
            render={(Error, SubmitButton) => (
              <Vertical>
                <Error code />
                <TextField
                  name="email"
                  defaultValue={email}
                  label={formatMessage(userAttributes.email)}
                />
                <ReCAPTCHA ref={recaptchaRef} />
                <SubmitButton disabled={!hasChanged} size="large">
                  <FormattedMessage {...messages.changeEmail} />
                </SubmitButton>
              </Vertical>
            )}
          />
        </Body>
      </Dialog>
      {!!otpRequiredForLogin && (
        <TwoFADialog
          onSubmit={(values, onResult) => {
            twoFACallback?.resolve(values.otpAttempt);
            onResult(values);
          }}
          onSuccess={() => setOpen2FA(false)}
          onClose={() => setOpen2FA(false)}
          onCancel={() => twoFACallback?.reject('no 2FA')}
          open={open2FA}
          reason={formatMessage(messages.changeEmail)}
        />
      )}
      {!!openDialogEmail && (
        <UpdateEmailDialog
          email={openDialogEmail}
          onClose={() => {
            setOpenDialogEmail(null);
          }}
        />
      )}
    </div>
  );
};
